import { API_USERS } from "globals/constants";
import { encodeSearchTerms, spUrl } from "utils";
import { buildSearchParams, request } from "utils/proxy";

/**
 * Represents a user object with personal, contact, affiliation, and role details.
 *
 * @typedef {Object} User
 *
 * @property {string} personId - Unique identifier for the person.
 * @property {string} tenantId - Unique identifier for the tenant.
 * @property {string} contactId - Unique identifier for the contact.
 * @property {string} contactEmail - Contact email address.
 * @property {string} firstName - First name of the person.
 * @property {string} middleName - Middle name of the person.
 * @property {string} lastName - Last name of the person.
 * @property {string} fullName - Full name of the person (includes middle name).
 * @property {boolean} active - Indicates if the user is currently active.
 * @property {string} userAccountId - Unique identifier for the user's account.
 * @property {string|null} userAccountLogin - The user's login identifier (null if not set).
 * @property {boolean|null} hideWarning - Indicates whether to hide warnings for this user (null if not applicable).
 * @property {string} employeeId - Employee ID associated with the user.
 * @property {string} adminUpdateDate - Date when the admin last updated the user account.
 * @property {string} contactAdminUpdateDate - Date when the admin last updated the contact info.
 * @property {string|null} demographicsId - Unique identifier for the user's demographics (null if not set).
 * @property {string|null} genderId - Unique identifier for the user's gender (null if not set).
 * @property {string|null} genderValue - Gender value (null if not set).
 * @property {string|null} ethnicityId - Unique identifier for the user's ethnicity (null if not set).
 * @property {string|null} ethnicityValue - Ethnicity value (null if not set).
 * @property {string|null} citizenshipId - Unique identifier for the user's citizenship (null if not set).
 * @property {string|null} citizenshipValue - Citizenship value (null if not set).
 * @property {string|null} demographicsUpdateDate - Date when demographics were last updated (null if not set).
 * @property {Affiliation[]} affiliations - Array of internal affiliations.
 * @property {ExternalAffiliation[]} externalAffiliations - Array of external affiliations.
 * @property {Role[]} roles - Array of roles assigned to the user.
 * @property {string[]} adHocTeams - Array of team IDs the user is associated with.
 * @property {string|null} profileTypes - Profile types associated with the user (null if not set).
 */

/**
 * Represents an affiliation within an organization.
 *
 * @typedef {Object} Affiliation
 * @property {string} affiliationId - Unique identifier for the affiliation.
 * @property {string} unitId - Unique identifier for the affiliated unit.
 * @property {string} title - Title of the affiliation (e.g., "Student").
 * @property {boolean} active - Indicates if the affiliation is active.
 * @property {boolean} primaryAppointment - Indicates if this is the primary appointment.
 * @property {string} adminUpdateDate - Date when the affiliation was last updated.
 * @property {string} unitName - Name of the affiliated unit.
 */

/**
 * Represents an external affiliation with an organization outside the main institution.
 *
 * @typedef {Object} ExternalAffiliation
 * @property {string} externalAffiliationId - Unique identifier for the external affiliation.
 * @property {string} organizationId - Unique identifier for the affiliated organization.
 * @property {string} title - Title of the external affiliation.
 * @property {boolean} active - Indicates if the external affiliation is active.
 * @property {string} adminUpdateDate - Date when the external affiliation was last updated.
 */

/**
 * Represents a role assigned to the user within the organization.
 *
 * @typedef {Object} Role
 * @property {string} groupName - Name of the group the user belongs to (e.g., "SP Award Reviewer").
 * @property {string} groupId - Unique identifier for the group.
 * @property {string|null} unitId - Unique identifier for the unit (null if not applicable).
 * @property {boolean} subUnits - Indicates whether the role applies to sub-units.
 */

export const users = {
	/**
   * Finds users based on search parameters.
   *
   * @param {Object} searchParams - The search parameters.
   * @param {Array<Object>} searchParams.search - An array of search filters where each object contains a key and value.
   * @param {string|number} [searchParams.page=0] - The page number for pagination.
   * @param {string|number} [searchParams.size=100] - The number of results per page.
   * @param {boolean} [searchParams.includeInactive] - Whether to include inactive users.
   *
   * @param {Object} [options] - Request options such as headers, method, etc.
   *
   * @returns {Promise<{ content: User[]; }>} - A promise that resolves with the response from the API.
   *
   * @example
   * await api.users.find({
   *   search: [ { key: "userAccountId", val: id } ],
   *   includeInactive: true,
   * });
   * OR
   * await api.users.find({
   *   search: [ { userAccountId: id } ],
   * });
   */
	find: (searchParams, options) => {
		const sp = {
			search: encodeSearchTerms(searchParams.search),
			page: parseInt(searchParams.page ?? "0", 10),
			size: parseInt(searchParams.size ?? "100", 10),
		};

		if (searchParams.includeInactive) {
			sp.activeOnly = false;
		}

		return request(spUrl(`${API_USERS}${buildSearchParams(sp)}`), options);
	},

	/**
   * Search users by full name
   *
   * @param {string} searchText - The search text.
   * @param {Object} [options] - Request options such as headers, method, etc.
   *
   * @returns {Promise<{ content: User[]; }>} - A promise that resolves with the response from the API.
   */
	searchByFullName: (searchText, options) => {
		const search = encodeSearchTerms([
			{
				key: "fullName",
				val: `${searchText
					.toLocaleLowerCase()
					.replace(/(\,|\*|\")/gi, "\\$1")
					.trim()}`,
			},
		]);

		return request(`${API_USERS}?search=${search}`, options);
	},
};
